import { FilterType } from "components/Reports/reportAssets/assets";
import {
  ControlerFilters,
  ControllersListQueryBody,
  newControllerData,
  updateControllerData,
} from "pages/ControlCenter/UsersPermissionsManagement/UsersPermissionsManagementComponents/controllers/controllerTypes";
import {
  UserApp,
  VolunteersListQueryBody,
} from "pages/ControlCenter/UsersPermissionsManagement/UsersPermissionsManagementComponents/volunteers/volunteerTypes";
import { getApiSLice } from "utils/getApiBaseQuery";

export const usersPermissionsManagementApiSlice = getApiSLice(
  "dev"
).injectEndpoints({
  endpoints: (builder) => ({
    getVolunteersList: builder.query({
      query: ({
        page,
        body,
      }: {
        page: number;
        body?: VolunteersListQueryBody;
      }) => {
        const requestBody = body || {};
        return {
          url: `/controlcenter/GetVolunteersData?page=${page}`,
          method: "POST",
          body: requestBody,
        };
      },
      providesTags: ["Volunteers"],
    }),
    importFromFile: builder.mutation({
      query: (data) => ({
        url: "/files/VerifyUsersFromMimshalZamin",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Volunteers"],
    }),
    disableEnableVolunteer: builder.mutation({
      query: ({
        userTz,
        isToDisable,
      }: {
        userTz: number;
        isToDisable: boolean;
      }) => {
        const url = isToDisable ? "DisableUser" : "EnableUser";
        return {
          url: `/controlcenter/${url}?userTz=${userTz}`,
          method: "POST",
        };
      },
      invalidatesTags: ["Volunteers"],
    }),
    disableEnableUserCamera: builder.mutation({
      query: ({ userId }: { userId: number }) => {
        return {
          url: `/controlcenter/ToggleUserCameraEntitlement?userId=${userId}`,
          method: "POST",
        };
      },
      invalidatesTags: ["Volunteers"],
    }),
    unlinkCamera: builder.mutation({
      query: ({ cameraUuid }: { cameraUuid: string }) => {
        return {
          url: `/controlcenter/UnlinkCameraFromVolunteer?cameraUuid=${cameraUuid}`,
          method: "POST",
        };
      },
      invalidatesTags: ["Volunteers"],
    }),
    updateVolunteer: builder.mutation({
      query: (data: UserApp) => ({
        url: "/controlcenter/UpdateVolunteerData",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Volunteers"],
    }),
    getControllersList: builder.query({
      query: ({ body }: { body?: ControllersListQueryBody }) => {
        const requestBody = body || {};
        return {
          url: `/controlcenter/GetControllersData`,
          method: "POST",
          body: requestBody,
        };
      },
      providesTags: ["Controllers"],
    }),
    disableEnableController: builder.mutation({
      query: ({
        controllerId,
        isDisable,
      }: {
        controllerId: string;
        isDisable: boolean;
      }) => {
        return {
          url: `controlcenter/ToggleControllerStatus?controllerId=${controllerId}&isDisable=${isDisable}`,
          method: "POST",
        };
      },
      invalidatesTags: ["Controllers"],
    }),
    updateController: builder.mutation({
      query: (data: updateControllerData) => ({
        url: "/controlcenter/UpdateControllerInfo",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Controllers"],
    }),
    addController: builder.mutation({
      query: (data: newControllerData) => ({
        url: "/loginApi/RegisterNewController",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Controllers"],
    }),
    addAutographPicController: builder.mutation({
      query: (data) => ({
        url: "/controlcenter/UploadControllerAutographPic",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Controllers"],
    }),
    deleteAutographPicController: builder.mutation({
      query: (controllerId: string) => ({
        url: `/controlcenter/DeleteControllerAutographPicture?controllerId=${controllerId}`,
        method: "POST",
      }),
      invalidatesTags: ["Controllers"],
    }),
    getControllersManagementFilters: builder.query({
      query: () => ({
        url: `/controlcenter/GetControllersManagementFilters`,
        method: "GET",
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Filters" as const, id })),
              "Filters",
            ]
          : ["Filters"],
    }),
    getVolunteersManagementFilters: builder.query({
      query: () => ({
        url: `/controlcenter/GetVolunteersManagementFilters`,
        method: "GET",
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "FiltersVolunteers" as const,
                id,
              })),
              "FiltersVolunteers",
            ]
          : ["FiltersVolunteers"],
    }),
    AddOrUpdateControllersManagementFilter: builder.mutation({
      query: (newFilter: ControlerFilters) => ({
        url: "/controlcenter/AddOrUpdateControllersManagementFilter",
        method: "POST",
        body: newFilter,
      }),
      invalidatesTags: ["Filters"],
    }),
    AddOrUpdateVolunteersManagementScreenFilter: builder.mutation({
      query: (newFilter: ControlerFilters) => ({
        url: "/controlcenter/AddOrUpdateVolunteersManagementScreenFilter",
        method: "POST",
        body: newFilter,
      }),
      invalidatesTags: ["FiltersVolunteers"],
    }),

    deleteManagementFilters: builder.mutation({
      query: (data: { idToDelete: number; filterType: FilterType }) => ({
        url: `/controlcenter/DeleteManagementFilters?filterId=${data.idToDelete}&screen=${data.filterType}`,
        method: "POST",
      }),
      invalidatesTags: ["Filters", "FiltersVolunteers"],
    }),

    getParameters: builder.query({
      query: () => ({
        url: `/backOffice/GetParameters`,
        method: "GET",
      }),
      providesTags: ["Parameters"],
    }),

    updateParameters: builder.mutation({
      query: (parametersToUpdate) => ({
        url: `/backOffice/UpdateParameters`,
        method: "POST",
        body: parametersToUpdate,
      }),
      invalidatesTags: ["Parameters"],
    }),
  }),
});

export const {
  useGetVolunteersListQuery,
  useImportFromFileMutation,
  useUpdateVolunteerMutation,
  useDisableEnableVolunteerMutation,
  useDisableEnableUserCameraMutation,
  useUnlinkCameraMutation,
  useGetControllersListQuery,
  useDisableEnableControllerMutation,
  useUpdateControllerMutation,
  useAddControllerMutation,
  useAddAutographPicControllerMutation,
  useDeleteAutographPicControllerMutation,
  useGetControllersManagementFiltersQuery,
  useGetVolunteersManagementFiltersQuery,
  useAddOrUpdateControllersManagementFilterMutation,
  useAddOrUpdateVolunteersManagementScreenFilterMutation,
  useDeleteManagementFiltersMutation,
  useGetParametersQuery,
  useUpdateParametersMutation,
} = usersPermissionsManagementApiSlice;
